import { AfterViewInit, Component, computed, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HeaderComponent } from '../../components/header/header.component';
import { ButtonComponent } from '../../components/button/button.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import TYPES from '../../constants/types';
import { AppointmentReportMeta, AppointmentReportPayload, AppointmentReportTotalsByCurrency, AppointmentReportLine, AppointmentReportPdfInput } from '../../types/appointment-report';
import { AppointmentsReportPdfService } from '../../services/appointments-report-pdf.service';

@Component({
  selector: 'app-appointments-report-view',
  imports: [
    HeaderComponent,
    ButtonComponent,
    LoadingComponent,
    TranslatePipe,
    DatePipe,
    DecimalPipe
  ],
  templateUrl: './appointments-report-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './appointments-report-view.component.scss'
})
export class AppointmentsReportViewComponent implements AfterViewInit {
  reportLines: WritableSignal<AppointmentReportLine[]> = signal<AppointmentReportLine[]>([]);
  reportTotals: WritableSignal<AppointmentReportTotalsByCurrency[]> = signal<AppointmentReportTotalsByCurrency[]>([]);
  reportMeta: WritableSignal<AppointmentReportMeta | null> = signal<AppointmentReportMeta | null>(null);
  loadingPdfAction: WritableSignal<boolean> = signal<boolean>(false);

  hasReport = computed(() => !!this.reportMeta());
  totalSessions = computed(() => this.reportLines().length);
  hasComments = computed(() => !!this.reportMeta()?.comments?.trim());

  private snackBar: MatSnackBar = inject(MatSnackBar);
  private translate: TranslateService = inject(TranslateService);
  private appointmentsReportPdfService: AppointmentsReportPdfService = inject(AppointmentsReportPdfService);

  BUTTON_TYPES: any;

  constructor(
    private router: Router
  ) {
    this.BUTTON_TYPES = TYPES.UI.BUTTON;

    const navigation = this.router.currentNavigation();
    const currentState = navigation?.extras.state ?? history.state;
    const reportPayload = currentState?.['report'] as AppointmentReportPayload | undefined;

    if(!reportPayload?.meta){
      this.router.navigate(['/appointments-report'], { replaceUrl: true });
      return;
    }

    this.reportLines.set(reportPayload.lines ?? []);
    this.reportTotals.set(reportPayload.totals ?? []);
    this.reportMeta.set({
      reportName: reportPayload.meta.reportName,
      patientName: reportPayload.meta.patientName,
      psychologistName: reportPayload.meta.psychologistName,
      psychologistEmail: reportPayload.meta.psychologistEmail,
      psychologistLicenseNumber: reportPayload.meta.psychologistLicenseNumber,
      psychologistLicenseCountry: reportPayload.meta.psychologistLicenseCountry,
      comments: reportPayload.meta.comments ?? '',
      startDate: new Date(reportPayload.meta.startDateIso),
      endDate: new Date(reportPayload.meta.endDateIso),
      generatedAt: new Date(reportPayload.meta.generatedAtIso)
    });
  }

  ngAfterViewInit(): void {
    // Ensure report screen always opens at the top regardless of previous page scroll.
    requestAnimationFrame(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }

  back(): void {
    history.back();
  }

  async downloadPdf(): Promise<void> {
    if(!this.hasReport()){
      return;
    }

    this.loadingPdfAction.set(true);

    try{
      const blob = await this.generatePdfBlob();
      this.downloadBlob(blob, this.buildPdfFileName());
      this.showSnackBar('appointments-report.pdf_downloaded');
    }catch{
      this.showSnackBar('appointments-report.pdf_generate_error');
    }finally{
      this.loadingPdfAction.set(false);
    }
  }

  async sharePdf(): Promise<void> {
    if(!this.hasReport()){
      return;
    }

    this.loadingPdfAction.set(true);

    let pdfBlob: Blob;

    try{
      pdfBlob = await this.generatePdfBlob();
    }catch{
      this.showSnackBar('appointments-report.pdf_generate_error');
      this.loadingPdfAction.set(false);
      return;
    }

    const fileName = this.buildPdfFileName();
    const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
    const shareData = {
      title: fileName,
      text: this.buildShareText(),
      files: [pdfFile]
    } as ShareData;

    const browserNavigator = navigator as Navigator & {
      canShare?: (data?: ShareData) => boolean;
      share?: (data?: ShareData) => Promise<void>;
    };

    const canShareFiles = typeof browserNavigator.share === 'function'
      && typeof browserNavigator.canShare === 'function'
      && browserNavigator.canShare({ files: [pdfFile] } as ShareData);

    if(!canShareFiles){
      this.downloadBlob(pdfBlob, fileName);
      this.showSnackBar('appointments-report.share_unavailable_downloaded');
      this.loadingPdfAction.set(false);
      return;
    }

    try{
      await browserNavigator.share(shareData);
    }catch(error: any){
      if(error?.name !== 'AbortError'){
        this.downloadBlob(pdfBlob, fileName);
        this.showSnackBar('appointments-report.share_unavailable_downloaded');
      }
    }finally{
      this.loadingPdfAction.set(false);
    }
  }

  private async generatePdfBlob(): Promise<Blob> {
    const meta = this.reportMeta();

    if(!meta){
      throw new Error('Missing report metadata.');
    }

    const pdfInput: AppointmentReportPdfInput = {
      lines: this.reportLines(),
      totals: this.reportTotals(),
      meta
    };

    return this.appointmentsReportPdfService.generateReportPdfBlob(pdfInput);
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }

  private showSnackBar(key: string): void {
    const message = this.translate.instant(key);
    const action = this.translate.instant('buttons.dismiss');
    this.snackBar.open(message, action);
  }

  getCurrencySymbol(code: string): string {
    const currency = TYPES.DATABASE.CURRENCY.find((item: any) => item.code === code);
    return currency ? currency.symbol : '';
  }

  private buildPdfFileName(): string {
    const meta = this.reportMeta();
    if(!meta){
      return 'appointments.pdf';
    }

    const patientName = this.normalizeFileNameChunk(meta.patientName || 'patient');
    const startDate = this.formatDateForFileName(meta.startDate);
    const endDate = this.formatDateForFileName(meta.endDate);

    return `${patientName}_${startDate}-${endDate}_appointments.pdf`;
  }

  private buildShareText(): string {
    const meta = this.reportMeta();
    if(!meta){
      return '';
    }

    const startDate = this.formatDateDisplay(meta.startDate);
    const endDate = this.formatDateDisplay(meta.endDate);

    return `${meta.reportName} - ${meta.patientName}\n${startDate} - ${endDate}`;
  }

  private formatDateForFileName(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  }

  private normalizeFileNameChunk(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .toLowerCase();
  }

  private formatDateDisplay(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${month}/${year}`;
  }
}
